import { useCallback, useEffect, useRef } from 'react';
import { getUserprint } from '@narvar/userprint';
import { DEBUG, getAppsGatewayUrl } from '../../../../../../../constants/env';
import { willSkipAnalytics } from '../../willSkipAnalytics';
import { APP_NAME, APP_VERSION, WORKFLOW_IDS } from './constants';

type FetchEventName =
  | 'widget_load'
  | 'widget_view'
  | 'cta_click'
  | 'offer_requested'
  | 'offer_displayed'
  | 'offer_declined'
  | 'form_closed'
  | 'checkout_handoff'
  | 'purchase_complete'
  | 'purchase_error';

// The plan the shopper committed to, sent alongside the hand-off so the chosen
// coverage and its price are answerable without Fetch's own reporting.
export interface FetchHandoffPlan {
  limit: string;
  deductible: string;
  reimbursement: string;
  billingFrequency: string;
  monthlyPrice?: number;
}

export type FetchFormCloseVia = 'close_button' | 'backdrop' | 'drawer';

export interface FetchFormClosed {
  msOnForm: number;
  closedVia: FetchFormCloseVia;
}

export interface FetchAnalyticsParams {
  jwt: string;
  sessionId: string;
  retailerMoniker: string;
  device: 'mobile' | 'desktop';
  pageUrl: string;
  versionName: string;
  locale: string;
  orderNumber?: string;
  trackingNumber?: string;
  trackingStatus?: string;
  decisionId?: string;
  rmFetchDesign?: string;
}

export function useFetchAnalytics(params: FetchAnalyticsParams) {
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const pageStartTime = useRef(performance.now());
  const firedRef = useRef<Set<string>>(new Set());
  const userprintRef = useRef<ReturnType<typeof getUserprint> | null>(null);

  const send = useCallback(
    async (eventName: FetchEventName, extraBody?: Record<string, unknown>) => {
      const p = paramsRef.current;
      if (willSkipAnalytics(p.pageUrl)) return;

      try {
        if (!userprintRef.current) {
          userprintRef.current = getUserprint();
        }
        const { browser_fingerprint: browserFingerprint } =
          await userprintRef.current;

        const event = {
          eventName,
          sessionId: p.sessionId,
          device: p.device,
          appName: APP_NAME,
          appVersion: APP_VERSION,
          pageUrl: p.pageUrl,
          versionName: p.versionName,
          locale: p.locale,
          browserFingerprint,
          eventOffsetMs: Math.round(performance.now() - pageStartTime.current),
          ...(p.orderNumber && { orderNumber: p.orderNumber }),
          ...(p.trackingNumber && { trackingNumber: p.trackingNumber }),
          ...(p.trackingStatus && { trackingStatus: p.trackingStatus }),
          ...(p.decisionId && { decisionId: p.decisionId }),
          ...(p.rmFetchDesign &&
            p.rmFetchDesign !== 'NOT_ENROLLED' && {
              experiments: { 'rm-fetch-design': p.rmFetchDesign },
            }),
        };

        await fetch(`${getAppsGatewayUrl()}/invoke`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${p.jwt}`,
          },
          body: JSON.stringify({
            workflowId: WORKFLOW_IDS.FETCH_EVENTS,
            context: { retailerMoniker: p.retailerMoniker },
            body: { ...event, ...extraBody },
          }),
        });
      } catch (err) {
        if (DEBUG) console.warn('Fetch analytics event failed:', err);
      }
    },
    [],
  );

  const sendOnce = useCallback(
    (eventName: FetchEventName) => {
      if (firedRef.current.has(eventName)) return;
      firedRef.current.add(eventName);
      void send(eventName);
    },
    [send],
  );

  const trackWidgetView = useCallback(
    () => sendOnce('widget_view'),
    [sendOnce],
  );
  const trackCtaClick = useCallback(() => void send('cta_click'), [send]);
  const trackOfferRequested = useCallback(
    () => void send('offer_requested'),
    [send],
  );
  const trackOfferDisplayed = useCallback(
    () => sendOnce('offer_displayed'),
    [sendOnce],
  );
  const trackOfferDeclined = useCallback(
    () => sendOnce('offer_declined'),
    [sendOnce],
  );
  // Not sendOnce: a shopper can open and close the form more than once in a
  // visit, and each close is its own measurement.
  const trackFormClosed = useCallback(
    (closed: FetchFormClosed) =>
      void send('form_closed', { payloadAttributes: closed }),
    [send],
  );
  const trackCheckoutHandoff = useCallback(
    (plan?: FetchHandoffPlan) =>
      void send(
        'checkout_handoff',
        plan ? { payloadAttributes: plan } : undefined,
      ),
    [send],
  );
  const trackPurchaseComplete = useCallback(
    () => void send('purchase_complete'),
    [send],
  );
  const trackPurchaseError = useCallback(
    (errorCode?: string) =>
      void send(
        'purchase_error',
        errorCode ? { errorDiagnostics: { errorCode } } : undefined,
      ),
    [send],
  );

  useEffect(() => {
    sendOnce('widget_load');
  }, [sendOnce]);

  return {
    trackWidgetView,
    trackCtaClick,
    trackOfferRequested,
    trackOfferDisplayed,
    trackOfferDeclined,
    trackFormClosed,
    trackCheckoutHandoff,
    trackPurchaseComplete,
    trackPurchaseError,
  };
}
